import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PublicActivity {
  id: string;
  type: 'new_member' | 'new_connection' | 'new_event' | 'new_opportunity' | 'new_workshop';
  title: string;
  description: string;
  created_at: string;
  user_name?: string;
}

export const usePublicActivity = (limit: number = 8) => {
  const [activities, setActivities] = useState<PublicActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        const allActivities: PublicActivity[] = [];

        // Nouveaux membres (derniers 30 jours)
        const { data: newMembers } = await supabase
          .from('club_members')
          .select('id, name, created_at')
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(3);

        if (newMembers) {
          newMembers.forEach(member => {
            allActivities.push({
              id: `member-${member.id}`,
              type: 'new_member',
              title: 'Nouveau membre',
              description: `${member.name} a rejoint la communauté`,
              created_at: member.created_at,
              user_name: member.name
            });
          });
        }

        // Nouvelles connexions acceptées
        const { data: connections } = await supabase
          .from('connection_requests')
          .select('id, created_at, updated_at, status, requester_id, requested_id')
          .eq('status', 'accepted')
          .order('updated_at', { ascending: false })
          .limit(3);

        if (connections) {
          // Récupérer les noms des membres connectés
          for (const conn of connections) {
            const { data: requesterData } = await supabase
              .from('club_members')
              .select('name')
              .eq('user_id', conn.requester_id)
              .single();
            
            const { data: requestedData } = await supabase
              .from('club_members')
              .select('name')
              .eq('user_id', conn.requested_id)
              .single();

            if (requesterData && requestedData) {
              allActivities.push({
                id: `connection-${conn.id}`,
                type: 'new_connection',
                title: 'Nouvelle connexion',
                description: `${requesterData.name} et ${requestedData.name} sont maintenant connectés`,
                created_at: conn.updated_at
              });
            }
          }
        }

        // Nouveaux événements
        const { data: events } = await supabase
          .from('events')
          .select('id, title, created_at')
          .order('created_at', { ascending: false })
          .limit(3);

        if (events) {
          events.forEach(event => {
            allActivities.push({
              id: `event-${event.id}`,
              type: 'new_event',
              title: 'Nouvel événement',
              description: event.title,
              created_at: event.created_at
            });
          });
        }

        // Nouvelles opportunités
        const { data: opportunities } = await supabase
          .from('opportunities')
          .select('id, title, created_at')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(3);

        if (opportunities) {
          opportunities.forEach(opp => {
            allActivities.push({
              id: `opportunity-${opp.id}`,
              type: 'new_opportunity',
              title: 'Nouvelle opportunité',
              description: opp.title,
              created_at: opp.created_at
            });
          });
        }

        // Nouveaux ateliers
        const { data: workshops } = await supabase
          .from('workshops')
          .select('id, title, created_at')
          .order('created_at', { ascending: false })
          .limit(3);

        if (workshops) {
          workshops.forEach(workshop => {
            allActivities.push({
              id: `workshop-${workshop.id}`,
              type: 'new_workshop',
              title: 'Nouvel atelier',
              description: workshop.title,
              created_at: workshop.created_at
            });
          });
        }

        // Trier toutes les activités par date et limiter
        const sortedActivities = allActivities
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, limit);

        setActivities(sortedActivities);
      } catch (err) {
        console.error('Error fetching public activities:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();
  }, [limit]);

  return { activities, isLoading, error };
};